import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import {
    printReports,
    getSignalColorFromDevice,
    getSignalTypeFromBits,
    getColorVariant,
    isBitSet,
} from "../../utils";
import StarekContext from "../../context/StarekContext";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
} from "recharts";
import moment from "moment";
import ReportResults from "./ReportResults";

export default function RawOnOffReport({
    filters,
    range,
    fetchRawData,
    onRefresh,
    shadowDevices,
    onFavoriteClick,
    onDownloadCSV,
    cachedDatapoints,
    isLoadingDatapoints,
    selectedDeviceIndex,
    onSelectedDeviceIndexChange,
}) {
    const { auth, lastUsedWorkspace, types } = useContext(StarekContext);
    const [rawData, setRawData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [aiAnalyzing, setAiAnalyzing] = useState(false);
    const [aiResults, setAiResults] = useState(null);
    const [aiError, setAiError] = useState(null);
    const [showAiPanel, setShowAiPanel] = useState(false);
    const currentDeviceIndex = selectedDeviceIndex;
    const setCurrentDeviceIndex = onSelectedDeviceIndexChange;
    const [hideEmptyDevices, setHideEmptyDevices] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [autoRefreshIntervalId, setAutoRefreshIntervalId] = useState(null);
    const printRef = useRef(null);

    const [visibleSignals, setVisibleSignals] = useState({});
    const [showThresholds, setShowThresholds] = useState(true);

    const handleDownloadCSV = () => {
        if (onDownloadCSV) onDownloadCSV();
    };

    const loadData = async () => {
        if (onRefresh) {
            const didUpdate = await onRefresh();
            if (didUpdate) return;
        }

        if (fetchRawData) {
            setLoading(true);
            fetchRawData()
                .then((data) => {
                    if (data && data.success) {
                        setRawData(data.data);
                        setCurrentDeviceIndex(0);
                    } else {
                        setRawData(null);
                    }
                })
                .catch((err) => {
                    console.error("Failed to fetch raw data", err);
                    setRawData(null);
                })
                .finally(() => setLoading(false));
        }
    };

    // Use cached datapoints from parent Reports component
    useEffect(() => {
        setLoading(isLoadingDatapoints);
        if (cachedDatapoints && cachedDatapoints.success) {
            setRawData(cachedDatapoints.data);
            // Don't reset device index here - let parent manage it for persistence
        } else if (cachedDatapoints === null && !isLoadingDatapoints) {
            setRawData(null);
        }
    }, [cachedDatapoints, isLoadingDatapoints]);

    // Setup auto-refresh interval
    useEffect(() => {
        if (autoRefresh && fetchRawData) {
            const intervalId = setInterval(() => {
                fetchRawData().catch((err) =>
                    console.error("Auto-refresh failed:", err),
                );
            }, 60000); // 60 seconds
            setAutoRefreshIntervalId(intervalId);

            return () => {
                if (intervalId) clearInterval(intervalId);
            };
        } else {
            // Clear interval if auto-refresh is disabled
            if (autoRefreshIntervalId) {
                clearInterval(autoRefreshIntervalId);
                setAutoRefreshIntervalId(null);
            }
        }
    }, [autoRefresh, fetchRawData]);

    const allDevices = useMemo(() => {
        // Use shadowDevices as the master list
        if (!shadowDevices || shadowDevices.length === 0) return [];
        if (!filters?.devices) return shadowDevices.map((d) => d.name || d.id);

        const selectedDeviceNames = filters.devices
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean);

        return shadowDevices
            .filter((dev) => {
                const deviceName = dev.name || dev.id;
                return selectedDeviceNames.includes(deviceName);
            })
            .map((d) => d.name || d.id);
    }, [shadowDevices, filters]);

    const deviceList = useMemo(() => {
        if (!hideEmptyDevices) return allDevices;
        return allDevices.filter((deviceName) => {
            const deviceSignals = rawData?.[deviceName];
            if (!deviceSignals) return false;
            // Check if device has any data points
            return Object.values(deviceSignals).some(
                (signals) => Array.isArray(signals) && signals.length > 0,
            );
        });
    }, [allDevices, rawData, hideEmptyDevices]);

    // Validate and clamp currentDeviceIndex to valid range
    const validatedDeviceIndex = useMemo(() => {
        if (deviceList.length === 0) return 0;
        return Math.min(currentDeviceIndex, deviceList.length - 1);
    }, [currentDeviceIndex, deviceList]);

    const currentDeviceName = deviceList[validatedDeviceIndex];

    // Get current device from shadowDevices (master source of truth)
    const currentShadowDevice = useMemo(() => {
        if (!currentDeviceName || !shadowDevices) return null;
        return shadowDevices.find(
            (dev) => (dev.name || dev.id) === currentDeviceName,
        );
    }, [currentDeviceName, shadowDevices]);

    const signalsWithColors = useMemo(() => {
        if (
            !currentDeviceName ||
            !currentShadowDevice ||
            !rawData?.[currentDeviceName]
        ) {
            return [];
        }

        // Use runtimeSignals from shadowDevice as master list
        const runtimeSignals =
            currentShadowDevice.device?.runtime?.signals || [];
        const signalsArray = Array.isArray(runtimeSignals)
            ? runtimeSignals
            : [];
        const defaultSignalColors = types?.device?.defaultSignalColors || [];
        const deviceSignalsData = rawData[currentDeviceName] || {};

        // Build signals list using shadowDevice metadata as master
        // Include ALL signals from shadowDevice, even if no data exists
        return signalsArray.map((signal, idx) => {
            const signalKey = signal.name;
            const hasData =
                deviceSignalsData[signalKey] &&
                Array.isArray(deviceSignalsData[signalKey]) &&
                deviceSignalsData[signalKey].length > 0;

            if (!hasData) {
                console.warn(`Signal "${signalKey}" has no data in rawData`);
            }

            // Determine signal type and styling based on signal.type integer
            let lineType = "monotone"; // default
            let strokeDasharray = undefined;
            let isDigital = false;

            // Extract inverted flag from parameters
            let params = currentShadowDevice.parameters;
            if (typeof params === "string") {
                try {
                    params = JSON.parse(params);
                } catch (e) {
                    params = {};
                }
            }
            const signalParams = params?.[signalKey];
            const isInverted = signalParams?.inverted || false;
            const minThreshold = signalParams?.minThreshold ?? 50;
            const maxThreshold = signalParams?.maxThreshold ?? 50;

            if (signal.type !== undefined && signal.type !== null) {
                // Call getSignalTypeFromBits to decode the type flags
                const typeFlags = getSignalTypeFromBits(signal.type, types);

                // Determine base line type: digital uses step, analog uses monotone
                const isDigitalFlag =
                    typeFlags.digital || isBitSet(signal.type, 2); // Bit 2: D (Discrete)
                isDigital = isDigitalFlag;

                const hasInput = typeFlags.input;
                const hasOutput = typeFlags.output;
                const isControl = typeFlags.control;
                const isStatus = typeFlags.status;

                if (isDigitalFlag) {
                    lineType = "stepAfter"; // Digital signals use step line
                } else if (hasInput && !hasOutput) {
                    lineType = "linear"; // Simple line for input-only analog
                } else {
                    lineType = "monotone"; // Monotone for analog (default)
                }

                // Apply dash pattern based on priority: status > control > output
                // Control dashes only apply to output signals (not input-only data)
                // Status dashes apply to status signals (even if input-only)
                if (isStatus) {
                    strokeDasharray = "3 3"; // Dashed line for status signals
                } else if (isControl && hasOutput) {
                    // Control dash only for output signals, not input-only data
                    strokeDasharray = "2 4"; // Dashes for control signals
                } else if (hasOutput && !hasInput) {
                    strokeDasharray = "5 5"; // Dotted line for output-only
                }
            }

            return {
                key: signalKey,
                name: signalKey,
                type: lineType,
                yAxisId: isDigital ? "right" : "left",
                color:
                    getSignalColorFromDevice(
                        currentShadowDevice,
                        signalKey,
                        idx,
                        defaultSignalColors,
                    ) || "#333333",
                strokeDasharray,
                isInverted,
                minThreshold,
                maxThreshold,
                thresholdColor: getColorVariant(
                    getSignalColorFromDevice(
                        currentShadowDevice,
                        signalKey,
                        idx,
                        defaultSignalColors,
                    ) || "#333333",
                    100,
                ), // Darkest color variant
                isDigital,
            };
        });
        // Remove the .filter() - keep all signals regardless of data availability
    }, [
        currentDeviceName,
        currentShadowDevice,
        rawData,
        types,
        currentShadowDevice?.parameters,
    ]);

    // Initialize visibleSignals when signalsWithColors changes
    useEffect(() => {
        if (signalsWithColors.length > 0) {
            const initialVisibleSignals = signalsWithColors.reduce(
                (acc, signal) => ({ ...acc, [signal.name]: true }),
                {},
            );
            setVisibleSignals(initialVisibleSignals);
        }
    }, [signalsWithColors]);

    const graphData = useMemo(() => {
        try {
            if (!currentDeviceName) return [];

            const deviceSignals = rawData ? rawData[currentDeviceName] : null;
            if (!deviceSignals) return [];

            const timeMap = new Map();

            signalsWithColors.forEach((signal) => {
                if (!signal) {
                    console.warn("Null signal in signalsWithColors");
                    return;
                }
                const points = deviceSignals[signal.name] || [];
                if (points.length === 0) {
                    return; // Skip this signal if no data found
                }
                points.forEach((point) => {
                    if (!timeMap.has(point.time)) {
                        timeMap.set(point.time, { time: point.time });
                    }
                    const entry = timeMap.get(point.time);

                    if (signal.yAxisId === "right") {
                        // Digital signal
                        let value = point.measure_value > 0 ? 1 : 0;
                        entry[signal.name] = signal.isInverted
                            ? 1 - value
                            : value;
                    } else {
                        // Analog signal: normalize to 0-100
                        let value = (point.measure_value / 1023) * 100;
                        entry[signal.name] = signal.isInverted
                            ? 100 - value
                            : value;
                    }
                });
            });

            return Array.from(timeMap.values()).sort((a, b) => a.time - b.time);
        } catch (error) {
            console.error("Error in graphData useMemo:", error);
            return [];
        }
    }, [rawData, currentDeviceName, signalsWithColors]);

    const xDomain = useMemo(() => {
        if (!filters?.durationStart || !range) return ["auto", "auto"];
        const baseTime = moment(filters.durationStart).unix();
        const start = baseTime + range[0];
        const end = baseTime + range[1];
        return [start, end];
    }, [filters, range]);

    const xTicks = useMemo(() => {
        const [start, end] = xDomain;
        if (start === "auto" || end === "auto") return undefined;

        const count = 6;
        const step = (end - start) / (count - 1);
        const ticks = [];
        for (let i = 0; i < count; i++) {
            ticks.push(Math.round(start + i * step));
        }
        return ticks;
    }, [xDomain]);

    const handlePrevDevice = () => {
        setCurrentDeviceIndex((prev) => (prev > 0 ? prev - 1 : prev));
    };

    const handleNextDevice = () => {
        setCurrentDeviceIndex((prev) =>
            prev < deviceList.length - 1 ? prev + 1 : prev,
        );
    };

    const handleLegendClick = (e) => {
        const { dataKey } = e;

        const isOnlyOneVisible =
            visibleSignals[dataKey] &&
            Object.values(visibleSignals).filter((v) => v).length === 1;

        if (isOnlyOneVisible) {
            setVisibleSignals(
                signalsWithColors.reduce(
                    (acc, signal) => ({ ...acc, [signal.name]: true }),
                    {},
                ),
            );
        } else {
            setVisibleSignals(
                signalsWithColors.reduce(
                    (acc, signal) => ({
                        ...acc,
                        [signal.name]: signal.name === dataKey,
                    }),
                    {},
                ),
            );
        }
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="tw-bg-white tw-p-2 tw-border tw-border-slate-200 tw-shadow-md tw-rounded tw-text-xs">
                    <p className="tw-font-semibold tw-mb-1">
                        {moment.unix(label).format("HH:mm:ss")}
                    </p>
                    {payload.map((entry, index) => {
                        const signalConfig = signalsWithColors.find(
                            (s) => s.key === entry.dataKey,
                        );
                        const isDigital = signalConfig?.yAxisId === "right";
                        const valueDisplay = isDigital
                            ? entry.value
                            : `${entry.value.toFixed(1)}%`;

                        return (
                            <div key={index} style={{ color: entry.color }}>
                                {entry.name}: {valueDisplay}
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    const handleAnalyzeWithAI = async () => {
        try {
            setAiAnalyzing(true);
            setAiError(null);
            setShowAiPanel(true);

            const { scheme, host, port } = lastUsedWorkspace.engine;
            const workspaceId = lastUsedWorkspace.name;

            const url = `${scheme}://${host}:${port}/api/v1/${workspaceId}/datapoints/ai-summary`;

            const payload = {
                input: "Analyze the device performance data and provide insights on trends, anomalies, and recommendations",
                data: rawData,
            };

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();
            console.log("AI Analysis Result:", result);
            setAiResults(result);
        } catch (error) {
            console.error("Error analyzing with AI:", error);
            setAiError("Failed to analyze data with AI. Please try again.");
            setAiResults(null);
        } finally {
            setAiAnalyzing(false);
        }
    };

    return (
        <ReportResults
            deviceList={deviceList}
            selectedDeviceIndex={validatedDeviceIndex}
            onPrevDevice={handlePrevDevice}
            onNextDevice={handleNextDevice}
            hideEmptyDevices={hideEmptyDevices}
            onHideEmptyDevicesChange={(checked) => {
                setHideEmptyDevices(checked);
                setCurrentDeviceIndex(0);
            }}
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            showThresholds={showThresholds}
            onShowThresholdsChange={setShowThresholds}
            loading={loading}
            onRefresh={loadData}
            onFavoriteClick={onFavoriteClick}
            onPrintClick={() => printReports(printRef)}
            onDownloadCSV={handleDownloadCSV}
            printRef={printRef}
            isAIAnalysisOpen={showAiPanel}
            isAIAnalyzing={aiAnalyzing}
            aiResults={aiResults}
            aiError={aiError}
            onCloseAIAnalysis={() => {
                setShowAiPanel(false);
                setAiResults(null);
                setAiError(null);
            }}
            onAIFollowUp={(question) => {
                console.log("Follow-up question:", question);
                // Follow-up logic can be implemented here
            }}
        >
            {/* Line Chart - Conditional rendering */}
            {loading ? (
                <div className="report-loading-container">
                    <span className="report-loading-text">Loading...</span>
                </div>
            ) : graphData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={graphData}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e0e0e0"
                        />
                        <XAxis
                            dataKey="time"
                            type="number"
                            domain={xDomain}
                            ticks={xTicks}
                            tickFormatter={(unixTime) =>
                                moment.unix(unixTime).format("HH:mm")
                            }
                            scale="time"
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis
                            yAxisId="left"
                            domain={[0, 100]}
                            tickFormatter={(val) => `${val}%`}
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={[0, 1]}
                            ticks={[0, 1]}
                            tick={{ fontSize: 12 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            onClick={handleLegendClick}
                            wrapperStyle={{ cursor: "pointer" }}
                        />
                        {/* Threshold reference lines for analog signals */}
                        {showThresholds &&
                            signalsWithColors
                                .filter((s) => s && !s.isDigital)
                                .map((signal) => [
                                    <ReferenceLine
                                        key={`${signal.name}-min-threshold`}
                                        yAxisId="left"
                                        y={signal.minThreshold}
                                        stroke={signal.color}
                                        strokeDasharray="4 4"
                                        strokeWidth={2.5}
                                        hide={!visibleSignals[signal.name]}
                                    />,
                                    <ReferenceLine
                                        key={`${signal.name}-max-threshold`}
                                        yAxisId="left"
                                        y={signal.maxThreshold}
                                        stroke={signal.color}
                                        strokeDasharray="4 4"
                                        strokeWidth={2.5}
                                        hide={!visibleSignals[signal.name]}
                                    />,
                                ])}
                        {signalsWithColors
                            .filter((s) => s && s.type)
                            .map((signal) => (
                                <Line
                                    key={signal.name}
                                    yAxisId={signal.yAxisId}
                                    type={signal.type}
                                    dataKey={signal.name}
                                    name={signal.name}
                                    stroke={signal.color}
                                    strokeDasharray={signal.strokeDasharray}
                                    dot={false}
                                    strokeWidth={2}
                                    connectNulls
                                    hide={!visibleSignals[signal.name]}
                                />
                            ))}
                    </LineChart>
                </ResponsiveContainer>
            ) : (
                <div className="report-loading-container">
                    <span className="report-loading-text">
                        No data available for selected device
                    </span>
                </div>
            )}
        </ReportResults>
    );
}
